import { API_BASE_URL } from '@/environment'
import { fastApiV1Fetch } from '@/service/fastApi'
import { customFetch } from '@/service/v3/apiV3Utils'
import { customFetchMedia } from '@/service/v3/apiV3UtilsMedia'
import type {
  CompanySettingsResponse,
  FastProductEnablementResponse,
} from './companySettingsTypes'

export const getCompanySettings = async (
  companyNameKey: string
): Promise<CompanySettingsResponse> => {
  const response = await customFetch(
    `${API_BASE_URL}/api/v3/admin/companies/${companyNameKey}/settings`,
    { method: 'GET' }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch company settings: ${response.statusText}`)
  }

  return response.json() as Promise<CompanySettingsResponse>
}

export const getFastProductEnablement =
  (): Promise<FastProductEnablementResponse> =>
    fastApiV1Fetch<FastProductEnablementResponse>(
      '/companies/current/product_enablement'
    )

export type CompanySettingsPatchAttributes = Partial<{
  portal_button_color: string
  portal_star_color: string
  default_portal_landing_page: string
}>

export type PatchCompanySettingsParams = {
  companyNameKey: string
  attributes: CompanySettingsPatchAttributes
}

export const patchCompanySettings = async ({
  companyNameKey,
  attributes,
}: PatchCompanySettingsParams): Promise<CompanySettingsResponse> => {
  const response = await customFetch(
    `${API_BASE_URL}/api/v3/admin/companies/${companyNameKey}/settings`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        data: {
          attributes,
        },
      }),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to update company settings: ${response.statusText}`)
  }

  return response.json() as Promise<CompanySettingsResponse>
}

export type PostCompanyImageParams = {
  companyNameKey: string
  file: File
}

const postCompanyImage = async (
  endpoint: string,
  { companyNameKey, file }: PostCompanyImageParams,
  errorLabel: string
) => {
  const form = new FormData()
  form.set('data[attributes][file]', file)

  const response = await customFetchMedia(
    `${API_BASE_URL}/api/v3/admin/companies/${companyNameKey}/${endpoint}`,
    {
      method: 'POST',
      body: form,
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to upload ${errorLabel}: ${response.statusText}`)
  }

  return response.json()
}

export const postCompanyLogo = (params: PostCompanyImageParams) =>
  postCompanyImage('logo', params, 'company logo')

export const postCompanyLoginBackground = (params: PostCompanyImageParams) =>
  postCompanyImage('login_background', params, 'company login background')

export const postCompanyPortalBackground = (params: PostCompanyImageParams) =>
  postCompanyImage('portal_background', params, 'company portal background')

export type DeleteCompanyPortalBackgroundParams = {
  companyNameKey: string
}

export const deleteCompanyPortalBackground = async ({
  companyNameKey,
}: DeleteCompanyPortalBackgroundParams): Promise<void> => {
  const response = await customFetch(
    `${API_BASE_URL}/api/v3/admin/companies/${companyNameKey}/portal_background`,
    { method: 'DELETE' }
  )

  if (!response.ok) {
    throw new Error(
      `Failed to remove company portal background: ${response.statusText}`
    )
  }
}
